import type { TFunction } from 'i18next';
import type { CognitiveFitness } from '../../model';
import parseAit from './parseAit';
import parseConcentrationGrid from './parseConcentrationGrid';
import parseDigitSpan from './parseDigitSpan';
import parseGoNoGo from './parseGoNoGo';
import parseNBack from './parseNBack';
import parseSart from './parseSart';
import parseSpatialPlanning from './parseSpatialPlanning';
import parseVisualReaction from './parseVisualReaction';
import {
    buildAitPlaceholder,
    buildConcentrationGridPlaceholder,
    buildDefaultCognitivePlaceholder,
    buildDefaultNBackPlaceholder,
    buildDigitSpanPlaceholder,
    buildGoNoGoPlaceholder,
} from './placeholders';
import type { AssessmentResultItemDataFragment } from '@/gql/graphql';

const parseCognitiveResults = (
    t: TFunction,
    items: AssessmentResultItemDataFragment[],
    incompleteItems: AssessmentResultItemDataFragment[] = []
): CognitiveFitness[] => {
    const incompleteModuleInstanceIds = new Set(incompleteItems.map(i => i.moduleInstance?.id));

    const completedItems = items.filter(
        i => !i.moduleInstance?.id || !incompleteModuleInstanceIds.has(i.moduleInstance.id)
    );

    const results: CognitiveFitness[] = [];
    // NBack
    const completedNBack = parseNBack(t, completedItems);
    if (completedNBack) {
        results.push(completedNBack);
    } else {
        const incompleteNBack = incompleteItems.find(i => i.moduleInstance?.moduleDefinitionIdentifier === 'nback');
        if (incompleteNBack) {
            const p = buildDefaultNBackPlaceholder(t, incompleteNBack);
            if (p) {
                results.push(p);
            }
        }
    }

    // SART
    const completedSart = parseSart(t, completedItems);
    if (completedSart) {
        results.push(completedSart);
    } else {
        const incompleteSart = incompleteItems.find(i => i.moduleInstance?.moduleDefinitionIdentifier === 'sart');
        if (incompleteSart) {
            const p = buildDefaultCognitivePlaceholder(t, incompleteSart);
            if (p) {
                results.push(p);
            }
        }
    }

    // Visual Reaction
    const completedVisualReaction = parseVisualReaction(t, completedItems);
    if (completedVisualReaction) {
        results.push(completedVisualReaction);
    } else {
        const incompleteVisualReaction = incompleteItems.find(
            i => i.moduleInstance?.moduleDefinitionIdentifier === 'visual-rxn'
        );
        if (incompleteVisualReaction) {
            const p = buildDefaultCognitivePlaceholder(t, incompleteVisualReaction);
            if (p) {
                results.push(p);
            }
        }
    }

    // Spatial Planning
    const completedSpatialPlanning = parseSpatialPlanning(t, completedItems);
    if (completedSpatialPlanning) {
        results.push(completedSpatialPlanning);
    } else {
        const incompleteSpatialPlanning = incompleteItems.find(
            i => i.moduleInstance?.moduleDefinitionIdentifier === 'spatial-planning'
        );
        if (incompleteSpatialPlanning) {
            const p = buildDefaultCognitivePlaceholder(t, incompleteSpatialPlanning);
            if (p) {
                results.push(p);
            }
        }
    }

    // Digit Span
    const completedDigitSpan = parseDigitSpan(t, completedItems);
    if (completedDigitSpan) {
        results.push(completedDigitSpan);
    } else {
        const incompleteDigitSpan = incompleteItems.find(
            i => i.moduleInstance?.moduleDefinitionIdentifier === 'digit-span'
        );
        if (incompleteDigitSpan) {
            const p = buildDigitSpanPlaceholder(t, incompleteDigitSpan);
            if (p) {
                results.push(p);
            }
        }
    }

    // Adaptive Intelligence Test (AIT)
    const completedAit = parseAit(t, completedItems);
    if (completedAit) {
        results.push(completedAit);
    } else {
        const incompleteAit = incompleteItems.find(
            i => i.moduleInstance?.moduleDefinitionIdentifier === 'adaptive-intelligence-test'
        );
        if (incompleteAit) {
            const p = buildAitPlaceholder(t, incompleteAit);
            if (p) {
                results.push(p);
            }
        }
    }

    // Go/No-Go (GNG)
    const completedGoNoGo = parseGoNoGo(t, completedItems);
    if (completedGoNoGo) {
        results.push(completedGoNoGo);
    } else {
        const incompleteGoNoGo = incompleteItems.find(i => i.moduleInstance?.moduleDefinitionIdentifier === 'go-no-go');
        if (incompleteGoNoGo) {
            const p = buildGoNoGoPlaceholder(t, incompleteGoNoGo);
            if (p) {
                results.push(p);
            }
        }
    }

    // Concentration Grid
    const completedConcentrationGrid = parseConcentrationGrid(t, completedItems);
    if (completedConcentrationGrid) {
        results.push(completedConcentrationGrid);
    } else {
        const incompleteConcentrationGrid = incompleteItems.find(
            i => i.moduleInstance?.moduleDefinitionIdentifier === 'concentration-grid'
        );
        if (incompleteConcentrationGrid) {
            const p = buildConcentrationGridPlaceholder(t, incompleteConcentrationGrid);
            if (p) {
                results.push(p);
            }
        }
    }

    return results;
};

export default parseCognitiveResults;
